import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { api } from '../../../services/api';
import { useAdminUsers } from '../../manageAdmin/context/AdminUsersContext';
import { AdminPermission } from '../../manageAdmin/AdminUserList.types';
import { User } from '../../../services/user.types';

export enum IssueType {
  Incomplete = 'Incomplete',
  RenderingProviderMissing = 'Rendering Provider Missing',
  RenderingProviderNPIMissing = 'Rendering Provider NPI Missing',
  BillingCodeMissing = 'Billing Code Missing',
  ProviderNotFound = 'Provider Not Found',
  PracticeNotFound = 'Practice Not Found',
  MissingCandidPrefix = 'Missing/Unresolved Candid Prefix',
  InsurerNameNotMapped = 'Insurer Name Not Mapped',
  CandidRejected = 'Candid Rejected',
  PlaceOfServiceCodeMissing = 'Place of Service Missing',
  BundleMismatch = 'Bundle Mismatch',
  WaitingToBeBundled = 'Waiting to be Bundled',
  Other = 'Other',
}

export type CandidSyncAttempt = {
  id: string;
  createdAt: Date;
  updatedAt?: Date;
  isActive: boolean;
  isExcluded?: boolean;
  excludedAt?: Date | null;
  excludedBy?: string | null;
  externalId: string;
  alohaAptId: number;
  renderingProviderName?: string;
  clientName: string;
  dateOfService: Date;
  billingCode?: string;
  issue?: string;
  issueType: IssueType;
};

export type CandidSyncProviderProps = {
  syncList: Array<CandidSyncAttempt>;
  fetchList: (options?: { excludedOnly?: boolean }) => Promise<void>;
};

export const CandidContext = createContext<CandidSyncProviderProps>({
  syncList: [],
  fetchList: async () => {},
});

export function CandidSyncProvider({ children }) {
  const { myPermissions } = useAdminUsers();
  const [syncList, setSyncList] = useState<Array<CandidSyncAttempt>>([]);

  const fetchList = useCallback(
    async (options?: { excludedOnly?: boolean }) => {
      if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_CANDID_SYNC)) {
        setSyncList(await api.getCandidSyncList(options));
      }
    },
    [myPermissions]
  );

  const values = useMemo(
    () => ({
      syncList,
      fetchList,
    }),
    [syncList, fetchList]
  );

  return <CandidContext.Provider value={values}>{children}</CandidContext.Provider>;
}

export function useCandidSyncList() {
  const context = useContext<CandidSyncProviderProps>(CandidContext);
  if (context === undefined) {
    throw new Error('useCandidSyncList must be used within a CandidSyncProvider');
  }
  return context;
}

export default useCandidSyncList;
